import { useEffect, useMemo } from 'react';

import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';

import { snakeToCamel } from '@shared/helpers';
import { getRootFiles } from '@widgets/Files/api/getRootFiles';
import {
    IBreadcrumb,
    IGetRootFilesApiResponse,
    IRawFile,
} from '@widgets/Files/model/types/api.interface';
import { IFIleEntity } from '@widgets/Files/model/types/fileEntity.interface';
import { TFolderId } from '@widgets/Files/model/types/folderId.type';

interface IUseGetFilesResponse {
    breadcrumbs: IBreadcrumb[];
    files: IRawFile[];
}

export const useGetFiles = (currentFolderId?: TFolderId | null) => {
    const { t } = useTranslation();
    const {
        isLoading: isDataLoading,
        isFetching,
        data: apiRes,
        isSuccess,
    } = useQuery<IGetRootFilesApiResponse<IUseGetFilesResponse>, Error>({
        queryKey: ['files', currentFolderId],
        queryFn: () => getRootFiles(currentFolderId),
        staleTime: 1000 * 60 * 5,
        refetchOnMount: true,
        refetchOnWindowFocus: false,
    });
    const parsedFilesData: IFIleEntity[] = useMemo(() => {
        if (!apiRes) return [];
        return apiRes?.data?.files?.map((file) => {
            const arr = Object.entries(file).map(([k, v]) => [snakeToCamel(k), v]);
            return Object.fromEntries(arr);
        });
    }, [apiRes]);
    const breadCrumbsData = useMemo(() => {
        if (!apiRes) return [];
        return apiRes?.data?.breadcrumbs;
    }, [apiRes]);
    useEffect(() => {
        if (apiRes?.status === 'error') {
            // @ts-ignore
            swal({
                text: apiRes.message,
                cancel: false,
                confirm: true,
                button: {
                    text: t('useFileUpload.return.swal.manyFiles.button'),
                    value: true,
                    visible: true,
                    className: 'btn btn-secondary',
                },
            });
        }
    }, [apiRes, t]);
    return { isDataLoading, isFetching, isSuccess, parsedFilesData, breadCrumbsData };
};
